import { NextPage } from 'next'
import Head from 'next/head'
import { useState, useEffect } from 'react'
import { t } from 'localization'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { RaffleDetail } from 'shared/types/raffles'
import { RaffleDetailPage } from 'shared/components/Raffles/RaffleDetailPage'
import { RafflePanel } from 'shared/components/Raffles/RafflePanel'
import { RaffleEntryModal } from 'shared/components/Raffles/RaffleEntryModal'
import { RaffleNotifyModal } from 'shared/components/Raffles/RaffleNotifyModal'
import {
  formatRaffleDate,
  formatRafflePriceForMeta,
  formatStoreList,
} from 'shared/components/Raffles/raffleUtils'
import { LoginPanelModal } from 'favorites/LoginPanelModal'
import { useCheckRaffleEntryQuery } from 'api/rafflesApi'
import { useUser } from 'shared/hooks/useUser'
import { GetServerPageProps } from 'shared/utils/getServerSideProps'
import { getRaffleDetailServerSideProps } from 'shared/utils/getRaffleDetailServerSideProps'
import { FC_STORES } from 'shared/constants/raffleStores'

const TP = 'shared.components.Raffles.RaffleDetailPage'

type RaffleDetailPageProps = GetServerPageProps & {
  raffle: RaffleDetail
}

const RaffleDetailRoute: NextPage<RaffleDetailPageProps> = ({ raffle, canonicalUrl }) => {
  const { currentUser } = useUser()
  const [showEntryModal, setShowEntryModal] = useState(false)
  const [showNotifyModal, setShowNotifyModal] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [pendingEntry, setPendingEntry] = useState(false)
  const [isCheckingEntry, setIsCheckingEntry] = useState(false)

  const { data: entryCheck, isFetching } = useCheckRaffleEntryQuery(
    { raffleSlug: raffle.slug },
    { skip: !currentUser?.id },
  )

  const hasEntered = entryCheck?.hasEntered ?? false

  useEffect(() => {
    sendTrackingEvent('DRAWING_DETAIL_VIEW', {
      product_name: raffle.productName,
      product_template_slug: raffle.slug,
      drawing_status: raffle.status,
      has_entered: hasEntered,
    })
    // Fire on raffle slug change (page nav); hasEntered is intentionally
    // not in the dep array so the view event isn't re-fired when the
    // RTK Query entry check resolves after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raffle.slug])

  const handleEnterRaffle = () => {
    sendTrackingEvent('DRAWING_ENTER_TAP', {
      product_name: raffle.productName,
      product_template_slug: raffle.slug,
      is_authenticated: !!currentUser?.id,
    })
    if (!currentUser?.id) {
      setPendingEntry(true)
      setShowLoginModal(true)
      return
    }
    setShowEntryModal(true)
  }

  const handleNotifyMe = () => {
    sendTrackingEvent('DRAWING_NOTIFY_TAP', {
      product_template_slug: raffle.slug,
      source: 'detail_page',
      drawing_status: raffle.status,
    })
    setShowNotifyModal(true)
  }

  const handleLoginModalClose = () => {
    setShowLoginModal(false)
    if (pendingEntry && currentUser?.id) {
      // User just logged in and wants to enter
      // RTK Query will automatically refetch since skip condition changed
      setIsCheckingEntry(true)
    }
  }

  useEffect(() => {
    if (isCheckingEntry && !isFetching && entryCheck) {
      setIsCheckingEntry(false)
      setPendingEntry(false)

      // Only open entry modal if user hasn't entered
      if (!entryCheck.hasEntered) {
        setShowEntryModal(true)
      }
      // If hasEntered is true, do nothing (button shows "Entered" state)
    }
  }, [isCheckingEntry, isFetching, entryCheck])

  const pageTitle = t(`${TP}.pageTitle`, {
    defaultValue: '{productName} Drawing | Flight Club',
    productName: raffle.productName,
  })

  const metaDescription = t(`${TP}.metaDescription`, {
    defaultValue:
      'Enter the {productName} {sku} Drawing at Flight Club for in store pickup in {cities} on {drawDate} for {price}.',
    productName: raffle.productName,
    sku: raffle.sku,
    cities: formatStoreList(raffle.stores || []),
    drawDate: formatRaffleDate(raffle.drawDate),
    price: formatRafflePriceForMeta(raffle.rafflePrice, raffle.currency || 'USD'),
  })

  const eventSchema = {
    '@context': 'http://schema.org',
    '@type': 'Event',
    name: `${raffle.productName} Drawing`,
    startDate: raffle.startDate,
    endDate: raffle.endDate,
    eventStatus: 'https://schema.org/EventScheduled',
    location: (raffle.stores || [])
      .filter((city) => FC_STORES[city])
      .map((city) => ({
        '@type': 'Place',
        name: FC_STORES[city].name,
        address: {
          '@type': 'PostalAddress',
          streetAddress: FC_STORES[city].address,
        },
      })),
    description: metaDescription,
  }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        {raffle.mainImage?.src && (
          <meta property="og:image" content={raffle.mainImage.src} />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(eventSchema) }}
        />
      </Head>

      <RaffleDetailPage
        raffle={raffle}
        panel={
          <RafflePanel
            raffle={raffle}
            variant="detail"
            hasEntered={hasEntered}
            isCheckingEntry={isCheckingEntry || isFetching}
            onEnterRaffle={handleEnterRaffle}
            onNotifyMe={handleNotifyMe}
          />
        }
      />

      {showEntryModal && (
        <RaffleEntryModal raffle={raffle} onClose={() => setShowEntryModal(false)} />
      )}

      {showNotifyModal && (
        <RaffleNotifyModal raffle={raffle} onClose={() => setShowNotifyModal(false)} />
      )}

      {showLoginModal && (
        <LoginPanelModal
          handleCloseLoginPanelModal={handleLoginModalClose}
          location="raffle_detail"
          flow="enterRaffle"
        />
      )}
    </>
  )
}

export const getServerSideProps = getRaffleDetailServerSideProps

export default RaffleDetailRoute
